// eslint-disable-next-line import/default
import type PopperJS from '@popperjs/core';
import { Size } from '@ps/web-core-enums';
import { gameEntity, useTrackers } from '@ps/web-shared-analytics';
import { Translation, useBodyScrollLock, useScreenSize } from '@ps/web-shared-components';
import classNames from 'clsx';
import maxSize from 'popper-max-size-modifier';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { usePopper } from 'react-popper';
import { useDispatch, useSelector } from 'react-redux';

// eslint-disable-next-line stars-web/no-restricted-alias-import
import { CasinoGameList } from '@swf/casino';
import { select } from '@swf/core/store';
import type { Dispatch } from '@swf/core/store';
import { maxHeight } from '@swf/core/utils/popperModifiers';

import { TXTWCL_No_matches_found } from './Search.messages.json';
import {
  active,
  arrow,
  autoComplete,
  gameList,
  highlight,
  item,
  mobile,
  noResults,
  overlay,
  popper,
  scroll
} from './Search.scss';
import Reference from './components/Reference/Reference';

const MIN_LENGTH = 3;

interface PopperParams {
  state: PopperJS.State;
  instance: PopperJS.Instance;
}

interface Props {
  className?: string;
  onToggle(): void;
}

const Search: React.FC<Props> = ({ className, onToggle }) => {
  const dispatch = useDispatch<Dispatch>();
  const searchConfig = useSelector(select.cms.getSearchConfig);
  const { search: searchResults } = useSelector(select.search.get) || {};
  const { searchClicked } = useTrackers();
  const [showGamesList, setShowGamesList] = useState(false);
  const [referenceElement, setReferenceElement] = useState<HTMLElement | null>(null);

  const [popperElement, setPopperElement] = useState<HTMLElement | null>(null);
  const [arrowElement, setArrowElement] = useState<HTMLElement | null>(null); // Don't change to useRef. It cause type issues and arrow not positioned correctly.

  const overlayRef = useRef<HTMLDivElement>(null);
  const [selected, setSelected] = useState<number>();
  const [query, setQuery] = useState('');

  const { greaterThan, windowSize } = useScreenSize();

  const isMobile = useSelector(select.device.isMobile);
  const isDesktopEmbedded = useSelector(select.device.isDesktopEmbedded);
  const [searchScrollElement, setSearchScrollElement] = useState<HTMLElement | null>(
    overlayRef.current
  );
  const { resultsViewed } = useTrackers();

  // Highlight part of the result with bold and prevent unterminated group with `{}()`
  const regex: '' | RegExp =
    query && RegExp(`(${query.replace(/[|\\{}()[\]^$+*?.]/g, '\\$&')})`, 'gi');

  const handleOnMouseMove = useCallback((index: number) => setSelected(index), []);

  useBodyScrollLock(isMobile, searchScrollElement);

  const modifiers = useMemo(
    () => [
      {
        name: 'arrow',
        enabled: !isDesktopEmbedded && !isMobile,
        options: { element: arrowElement }
      },
      {
        name: 'offset',
        options: { offset: [0, (isDesktopEmbedded && !showGamesList) || isMobile ? 0 : 8] }
      },
      {
        name: 'sameWidth',
        enabled: (isDesktopEmbedded && !showGamesList) || isMobile,
        fn: ({ state }: PopperParams) => {
          state.styles.popper.width = `${state.rects.reference.width}px`;
        },
        phase: 'beforeWrite' as const
      },
      {
        name: 'offset',
        options: { offset: [0, isDesktopEmbedded || isMobile ? 0 : 8] }
      },
      {
        name: 'preventOverflow',
        options: { rootBoundary: 'document' }
      },
      maxSize,
      maxHeight({ bottomSpacing: 15 })
    ],
    [arrowElement, isDesktopEmbedded, isMobile, showGamesList]
  );

  const { styles, attributes, update } = usePopper(referenceElement, popperElement, {
    placement:
      isDesktopEmbedded || isMobile ? (showGamesList ? 'bottom' : 'bottom-start') : 'bottom-end',
    modifiers
  });

  useEffect(() => {
    if ((searchResults?.length && query.length < MIN_LENGTH) || showGamesList || !popperElement) {
      return;
    }
    const selectedNode = popperElement.children[0].children[selected ?? 0];
    if (selectedNode && selected !== undefined) {
      selectedNode.scrollIntoView({ block: 'nearest' });
    }
  }, [popperElement, query.length, searchResults?.length, selected, showGamesList]);

  useEffect(() => {
    searchResults?.length &&
      resultsViewed(
        { userInput: query, totalResults: searchResults.length },
        searchResults.map(gameEntity)
      );
  }, [resultsViewed, query, searchResults]);

  useEffect(() => {
    // Redraw popper when with new search results or window size changes
    windowSize && searchResults && showGamesList && update && update();
  }, [windowSize, searchResults, showGamesList, update]);

  useEffect(() => {
    setSearchScrollElement(popperElement || overlayRef.current);
  }, [popperElement]);

  const clear = useCallback(() => {
    dispatch.search.clear();
    setSelected(undefined);
  }, [dispatch.search]);

  const selectGame = useCallback(
    (name: string, e?: React.SyntheticEvent) => {
      e?.stopPropagation();
      clear(); // Prevent thumbnails flickering, always show relevant results
      setQuery(name);
      setShowGamesList(true);
      searchClicked({ buttonName: name, search: query });
    },
    [clear, query, searchClicked]
  );

  const handleClear = useCallback(
    (e: Event) => {
      const target = e.target as Node;
      if (!(referenceElement?.contains(target) || popperElement?.contains(target))) {
        clear();
        setQuery('');
      }
    },
    [clear, popperElement, referenceElement]
  );

  const handleResize = useCallback(() => {
    // Handle virtual keyboard show/hide
    update && update();
  }, [update]);

  useEffect(() => {
    if (query.length < MIN_LENGTH) {
      query === '' && clear();
      return;
    }
    dispatch.search.execute(query);
  }, [query, clear, dispatch.search]);

  useEffect(() => {
    if (query.length < MIN_LENGTH) {
      clear(); // Force Popper re-open when switching between list to thumbnails
      setShowGamesList(false);
    }
  }, [query.length, clear]);

  useEffect(() => {
    window.addEventListener('click', handleClear);
    return () => window.removeEventListener('click', handleClear);
  }, [handleClear]);

  useEffect(() => {
    // TODO: the type is missing in lib.dom.d.ts at this moment. Figure out a better type solution.
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const vp = (window as any).visualViewport;
    if (!vp) {
      return;
    }
    vp.addEventListener('resize', handleResize);
    vp.removeEventListener('click', handleResize);
  }, [handleResize]);
  return (
    <>
      {isMobile && <div className={overlay} ref={overlayRef} />}
      <Reference
        query={query}
        setQuery={setQuery}
        searchToggle={onToggle}
        className={className}
        showOverlay={isMobile}
        referenceElement={referenceElement}
        setReferenceElement={setReferenceElement}
        results={searchResults}
        showGamesList={showGamesList}
        selected={selected}
        setSelected={setSelected}
        selectGame={selectGame}
        setShowGamesList={setShowGamesList}
      />
      {searchResults && (
        <div
          className={classNames(popper, {
            [autoComplete]: searchResults?.length && !showGamesList,
            [gameList]: searchResults?.length && showGamesList,
            [mobile]: isMobile
          })}
          ref={setPopperElement}
          style={styles.popper}
          {...attributes.popper}
        >
          {searchResults?.length === 0 && (
            <span className={classNames(noResults)}>
              <Translation {...TXTWCL_No_matches_found} />
            </span>
          )}
          {searchResults?.length > 0 &&
            (showGamesList ? (
              <CasinoGameList
                shrink={greaterThan(Size.Small)}
                icon="icnSquare"
                preload
                games={searchResults}
                className={scroll}
                overlayHierarchy={searchConfig?.overlayHierarchy}
                newGameTileViewOverride={searchConfig?.newGameTileView}
                disableIconsOverride={searchConfig?.disableIcons}
                disableProviderLogosOverride={searchConfig?.disableProviderLogos}
              />
            ) : (
              <ul onMouseLeave={() => setSelected(undefined)}>
                {searchResults.map(({ name }, index) => (
                  <li
                    key={index}
                    className={classNames(item, selected === index && active)}
                    onClick={(e) => selectGame(name, e)}
                    onMouseMove={() => handleOnMouseMove(index)}
                  >
                    {name.split(regex as RegExp).map((part, i) => (
                      <span
                        key={i}
                        className={classNames(
                          part.toLowerCase() === query.toLocaleLowerCase() && highlight
                        )}
                      >
                        {part}
                      </span>
                    ))}
                  </li>
                ))}
              </ul>
            ))}
          {!isDesktopEmbedded && !isMobile && (
            <div className={arrow} ref={setArrowElement} style={styles.arrow} />
          )}
        </div>
      )}
    </>
  );
};

export default Search;
